// src/components/chat/CustomerChatWidget.tsx
"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  MessageCircle,
  X,
  Phone,
  Video,
  Paperclip,
  Send,
  Mic,
  CheckCheck,
  Loader2,
  PhoneOff,
  MicOff,
  VideoOff,
  PhoneCall,
  Download,
  FileSpreadsheet,
  FileCode,
  FileText,
  Trash2,
  PhoneIncoming,
  PhoneMissed,
  Bot,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

interface CustomerChatWidgetProps {
  subdomain?: string;
}

interface QuickPromptItem {
  label: string;
  prompt: string;
}

const DEFAULT_QUICK_PROMPTS: QuickPromptItem[] = [
  { label: "🛍️ সব প্রোডাক্ট", prompt: "আপনাদের স্টোরে কি কি প্রোডাক্ট পাওয়া যায়?" },
  { label: "🔥 ডিসকাউন্ট অফার", prompt: "বর্তমানে কোন কোন পণ্যে ডিসকাউন্ট অফার চলছে?" },
  { label: "🚚 ডেলিভারি চার্জ", prompt: "ঢাকার ভেতরে ও ঢাকার বাইরে ডেলিভারি চার্জ কত?" },
  { label: "📦 অর্ডার ট্র্যাকিং", prompt: "আমার অর্ডার স্ট্যাটাস ট্র্যাক করতে চাই" },
  { label: "🏪 স্টোরের ঠিকানা", prompt: "আপনাদের স্টোরের ঠিকানা ও যোগাযোগের নাম্বার দিন" },
  { label: "💰 কম দামের পণ্য", prompt: "সবচেয়ে কম দামের প্রোডাক্ট কোনগুলো?" },
];

const CUSTOM_DOMAIN_MAP: Record<string, string> = {
  "buynarabd.com": "buynarabd",
  "www.buynarabd.com": "buynarabd",
  "bestbdbazar.com": "bestbdbazar",
  "www.bestbdbazar.com": "bestbdbazar",
};

const ICE_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

class CallTonePlayer {
  private ctx: AudioContext | null = null;
  private intervalId: any = null;

  playIncoming() {
    this.stop();
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      this.ctx = new AudioCtx();
      const playChime = () => {
        if (!this.ctx || this.ctx.state === "closed") return;
        const now = this.ctx.currentTime;
        [853, 960].forEach((freq) => {
          const osc = this.ctx!.createOscillator();
          const gain = this.ctx!.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, now);
          gain.gain.setValueAtTime(0.18, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 1.2);
          osc.connect(gain);
          gain.connect(this.ctx!.destination);
          osc.start(now);
          osc.stop(now + 1.2);
        });
      };
      playChime();
      this.intervalId = setInterval(playChime, 2400);
    } catch {}
  }

  playOutgoing() {
    this.stop();
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      this.ctx = new AudioCtx();
      const playBeep = () => {
        if (!this.ctx || this.ctx.state === "closed") return;
        const now = this.ctx.currentTime;
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(425, now);
        gain.gain.setValueAtTime(0.12, now);
        gain.gain.setValueAtTime(0.12, now + 0.8);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.85);
        osc.connect(gain);
        gain.connect(this.ctx!.destination);
        osc.start(now);
        osc.stop(now + 0.85);
      };
      playBeep();
      this.intervalId = setInterval(playBeep, 2600);
    } catch {}
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.ctx && this.ctx.state !== "closed") {
      this.ctx.close().catch(() => {});
      this.ctx = null;
    }
  }
}

const tonePlayer = new CallTonePlayer();

function renderInlineFormattedTokens(textSegment: string, keyPrefix: string) {
  const tokenRegex = /(\*\*.*?\*\*|~~.*?~~|`.*?`|_[^_\s][^_]*?_)/g;
  const tokens = textSegment.split(tokenRegex);

  return tokens.map((tok, idx) => {
    const k = `${keyPrefix}_${idx}`;
    if (tok.startsWith("**") && tok.endsWith("**") && tok.length > 4) {
      return (
        <strong key={k} className="font-bold text-white">
          {tok.slice(2, -2)}
        </strong>
      );
    }
    if (tok.startsWith("~~") && tok.endsWith("~~") && tok.length > 4) {
      return (
        <span key={k} className="line-through text-slate-400 text-[11px] mx-0.5">
          {tok.slice(2, -2)}
        </span>
      );
    }
    if (tok.startsWith("`") && tok.endsWith("`") && tok.length > 2) {
      return (
        <code
          key={k}
          className="px-1.5 py-0.5 rounded bg-slate-950/80 text-emerald-300 font-mono text-[10px] border border-slate-700"
        >
          {tok.slice(1, -1)}
        </code>
      );
    }
    if (tok.startsWith("_") && tok.endsWith("_") && tok.length > 2) {
      return (
        <em key={k} className="italic text-slate-300 text-[11px]">
          {tok.slice(1, -1)}
        </em>
      );
    }
    return tok;
  });
}

function FormattedChatText({ content }: { content: string }) {
  if (!content) return null;
  const lines = content.split("\n");

  return (
    <div className="space-y-1">
      {lines.map((line, lIdx) => {
        if (!line.trim()) {
          return <div key={lIdx} className="h-1.5" />;
        }

        const linkRegex = /\[(.*?)\]\((.*?)\)/g;
        const parts: any[] = [];
        let lastIndex = 0;
        let match;

        while ((match = linkRegex.exec(line)) !== null) {
          if (match.index > lastIndex) {
            parts.push(line.substring(lastIndex, match.index));
          }
          const linkText = match[1];
          const linkUrl = match[2];
          parts.push(
            <a
              key={`link_${lIdx}_${match.index}`}
              href={linkUrl}
              className="inline-flex items-center gap-1.5 font-bold text-emerald-300 hover:text-white bg-emerald-950/70 hover:bg-emerald-600/80 px-2.5 py-1 rounded-xl text-[11px] my-1 transition-all cursor-pointer border border-emerald-500/40 active:scale-95 shadow-xs"
            >
              <span>{linkText}</span>
              <ExternalLink className="w-3 h-3 text-emerald-300" />
            </a>
          );
          lastIndex = linkRegex.lastIndex;
        }

        if (lastIndex < line.length) {
          parts.push(line.substring(lastIndex));
        }

        return (
          <p key={lIdx} className="leading-relaxed break-words">
            {parts.map((part, pIdx) => {
              if (typeof part === "string") {
                return renderInlineFormattedTokens(part, `l${lIdx}_p${pIdx}`);
              }
              return part;
            })}
          </p>
        );
      })}
    </div>
  );
}

export default function CustomerChatWidget({ subdomain = "nextlook" }: CustomerChatWidgetProps) {
  const { user, token, openAuthModal } = useAuth();

  const activeSubdomain = useMemo(() => {
    if (
      subdomain &&
      !["nextlook", "custom-domain", "localhost", "www", ""].includes(subdomain.toLowerCase())
    ) {
      return subdomain.toLowerCase().split(".")[0];
    }
    if (typeof window !== "undefined") {
      const host = window.location.hostname.toLowerCase();
      if (CUSTOM_DOMAIN_MAP[host]) return CUSTOM_DOMAIN_MAP[host];
      if (host.endsWith(".localhost")) return host.split(".")[0];
      if (host.includes(".shop.")) return host.split(".shop.")[0];
      const cleanHost = host.replace(/^www\./, "");
      if (CUSTOM_DOMAIN_MAP[cleanHost]) return CUSTOM_DOMAIN_MAP[cleanHost];
      if (!cleanHost.includes("localhost") && cleanHost !== "127.0.0.1") {
        return cleanHost.split(".")[0];
      }
    }
    return subdomain || "store";
  }, [subdomain]);

  const [isOpen, setIsOpen] = useState(false);
  const [roomId, setRoomId] = useState<number | null>(null);
  const [merchantId, setMerchantId] = useState<number | string | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [merchantName, setMerchantName] = useState("Store Support");
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [quickPrompts, setQuickPrompts] = useState<QuickPromptItem[]>(DEFAULT_QUICK_PROMPTS);

  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<any>(null);
  const isCancelledRef = useRef(false);

  const [callState, setCallState] = useState<"idle" | "calling" | "ringing" | "active">("idle");
  const [callType, setCallType] = useState<"audio" | "video">("audio");
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);

  const socketRef = useRef<WebSocket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const pendingCandidatesRef = useRef<RTCIceCandidateInit[]>([]);

  const apiBase = useMemo(() => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.includes("localhost") || host === "127.0.0.1") {
        return `http://${activeSubdomain}.localhost:8000`;
      }
      return "";
    }
    return `http://${activeSubdomain}.localhost:8000`;
  }, [activeSubdomain]);

  const wsBase = useMemo(() => {
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host.includes("localhost") || host === "127.0.0.1") {
        return `ws://${activeSubdomain}.localhost:8000`;
      }
      const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
      return `${proto}//${window.location.host}`;
    }
    return `ws://${activeSubdomain}.localhost:8000`;
  }, [activeSubdomain]);

  const getSafeAuthToken = useCallback(() => {
    if (token) return token;
    if (typeof window !== "undefined") {
      return (
        localStorage.getItem(`xylo_token_${activeSubdomain}`) ||
        localStorage.getItem(`xylo_jwt_access_${activeSubdomain}`) ||
        localStorage.getItem("access_token") ||
        localStorage.getItem("token") ||
        localStorage.getItem("customer_token") ||
        ""
      );
    }
    return "";
  }, [activeSubdomain, token]);

  const formatMediaUrl = useCallback(
    (url?: string) => {
      if (!url) return "";
      let path = url.trim();
      if (path.startsWith("http://") || path.startsWith("https://")) {
        try {
          const parsed = new URL(path);
          path = parsed.pathname + parsed.search;
        } catch {
          path = path.replace(/^https?:\/\/[^\/]+/, "");
        }
      }
      if (!path.startsWith("/")) path = `/${path}`;
      if (typeof window !== "undefined") {
        const host = window.location.hostname;
        if (host.includes("localhost") || host === "127.0.0.1") {
          return `http://${activeSubdomain}.localhost:8000${path}`;
        }
        return path;
      }
      return path;
    },
    [activeSubdomain]
  );

  useEffect(() => {
    if (callState === "ringing") tonePlayer.playIncoming();
    else if (callState === "calling") tonePlayer.playOutgoing();
    else tonePlayer.stop();
    return () => tonePlayer.stop();
  }, [callState]);

  useEffect(() => {
    if (callState === "active") {
      if (localVideoRef.current && localStreamRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
      if (remoteVideoRef.current && remoteStreamRef.current) {
        remoteVideoRef.current.srcObject = remoteStreamRef.current;
      }
      if (remoteAudioRef.current && remoteStreamRef.current) {
        remoteAudioRef.current.srcObject = remoteStreamRef.current;
        remoteAudioRef.current.play().catch(() => {});
      }
    }
  }, [callState, callType]);

  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    setRoomId(null);
    setMessages([]);
    setCallState("idle");
    setIsBotTyping(false);
    if (!user) setIsOpen(false);
  }, [user?.id]);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen, isBotTyping]);

  useEffect(() => {
    let interval: any;
    if (callState === "active") {
      interval = setInterval(() => setCallDuration((p) => p + 1), 1000);
    } else {
      setCallDuration(0);
    }
    return () => clearInterval(interval);
  }, [callState]);

  const formatTimer = (sec: number) => {
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const formatValidDate = (dateString: any) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? "" : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const cleanupMedia = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    remoteStreamRef.current = null;
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
    if (localVideoRef.current) localVideoRef.current.srcObject = null;
    pendingCandidatesRef.current = [];
    tonePlayer.stop();
  }, []);

  const createPeerConnection = useCallback(
    (currentSocket: WebSocket, type: "audio" | "video") => {
      const pc = new RTCPeerConnection(ICE_SERVERS);
      pc.ontrack = (event) => {
        const remoteStream = event.streams[0];
        remoteStreamRef.current = remoteStream;
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = remoteStream;
          remoteAudioRef.current.play().catch(() => {});
        }
        if (type === "video" && remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = remoteStream;
        }
      };
      pc.onicecandidate = (event) => {
        if (event.candidate && currentSocket.readyState === WebSocket.OPEN) {
          currentSocket.send(
            JSON.stringify({
              type: "ice_candidate",
              data: { candidate: event.candidate, sender_id: user?.id },
            })
          );
        }
      };
      peerConnectionRef.current = pc;
      return pc;
    },
    [user?.id]
  );

  useEffect(() => {
    if (!isOpen) return;
    const authToken = getSafeAuthToken();
    fetch(
      `${apiBase}/api/v1/chat/store-context/?subdomain=${encodeURIComponent(activeSubdomain)}`,
      {
        headers: {
          Accept: "application/json",
          "X-Tenant-Subdomain": activeSubdomain,
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      }
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && Array.isArray(data.quick_prompts) && data.quick_prompts.length > 0) {
          setQuickPrompts(data.quick_prompts);
        }
      })
      .catch(() => {});
  }, [isOpen, apiBase, activeSubdomain, getSafeAuthToken]);

  useEffect(() => {
    if (!user || !isOpen) return;
    let isMounted = true;

    async function loadChatSession() {
      setLoading(true);
      try {
        const authToken = getSafeAuthToken();
        const headers: Record<string, string> = {
          "Content-Type": "application/json",
          "X-Tenant-Subdomain": activeSubdomain,
        };
        if (authToken) headers["Authorization"] = `Bearer ${authToken}`;

        const res = await fetch(
          `${apiBase}/api/v1/chat/rooms/create/?subdomain=${encodeURIComponent(activeSubdomain)}`,
          {
            method: "POST",
            headers,
            credentials: "include",
            body: JSON.stringify({
              user_id: user.id,
              email: user.email,
              name: user.name || `${user.first_name || ""} ${user.last_name || ""}`.trim(),
              subdomain: activeSubdomain,
            }),
          }
        );

        if (!res.ok) return;
        const room = await res.json();
        if (!isMounted) return;

        setRoomId(room.id);
        setMerchantId(room.merchant_details?.id || room.merchant);
        setMerchantName(room.merchant_details?.name || "Store Support");

        const msgRes = await fetch(
          `${apiBase}/api/v1/chat/rooms/${room.id}/messages/?subdomain=${encodeURIComponent(activeSubdomain)}`,
          { headers }
        );
        if (msgRes.ok && isMounted) {
          const msgData = await msgRes.json();
          setMessages(Array.isArray(msgData) ? msgData : msgData.results || []);
        }

        fetch(
          `${apiBase}/api/v1/chat/rooms/${room.id}/read/?subdomain=${encodeURIComponent(activeSubdomain)}`,
          { method: "POST", headers }
        ).catch(() => {});

        if (socketRef.current) socketRef.current.close();
        cleanupMedia();

        try {
          const ws = new WebSocket(
            `${wsBase}/ws/chat/${room.id}/?token=${authToken}&subdomain=${encodeURIComponent(activeSubdomain)}`
          );
          socketRef.current = ws;

          ws.onmessage = async (event) => {
            const parsed = JSON.parse(event.data);
            const signalData = parsed.data?.data || parsed.data || {};

            if (parsed.type === "chat_message" && parsed.message) {
              setIsBotTyping(false);
              setMessages((prev) => {
                if (prev.some((m) => String(m.id) === String(parsed.message.id))) return prev;
                const cleaned = prev.filter(
                  (m) => !(String(m.id).startsWith("temp_") && m.text === parsed.message.text)
                );
                return [...cleaned, parsed.message];
              });
            } else if (parsed.type === "typing") {
              if (parsed.sender_id === "bot" || String(parsed.sender_id) !== String(user.id)) {
                setIsBotTyping(Boolean(parsed.is_typing));
              }
            } else if (parsed.type === "call_offer") {
              if (String(signalData.sender_id) !== String(user.id)) {
                setCallType(signalData.call_type || "audio");
                setCallState("ringing");
                (window as any).__pendingOfferWidget = signalData.sdp;
              }
            } else if (parsed.type === "call_answer") {
              if (String(signalData.sender_id) !== String(user.id)) {
                setCallState("active");
                if (peerConnectionRef.current && signalData.sdp) {
                  await peerConnectionRef.current.setRemoteDescription(
                    new RTCSessionDescription(signalData.sdp)
                  );
                  while (pendingCandidatesRef.current.length > 0) {
                    const cand = pendingCandidatesRef.current.shift();
                    if (cand) await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(cand));
                  }
                }
              }
            } else if (parsed.type === "ice_candidate") {
              if (String(signalData.sender_id) !== String(user.id)) {
                const candidate = signalData.candidate;
                if (candidate) {
                  if (peerConnectionRef.current && peerConnectionRef.current.remoteDescription) {
                    await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(candidate));
                  } else {
                    pendingCandidatesRef.current.push(candidate);
                  }
                }
              }
            } else if (parsed.type === "call_ended" || parsed.type === "call_rejected") {
              cleanupMedia();
              setCallState("idle");
            }
          };
        } catch {}
      } catch (err) {
        console.error("Chat error:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadChatSession();

    return () => {
      isMounted = false;
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      cleanupMedia();
    };
  }, [user?.id, isOpen, apiBase, wsBase, activeSubdomain, cleanupMedia, getSafeAuthToken, user?.email, user?.name, user?.first_name, user?.last_name]);

  useEffect(() => {
    if (!isOpen || !roomId || !user) return;
    const interval = setInterval(async () => {
      try {
        const authToken = getSafeAuthToken();
        const res = await fetch(
          `${apiBase}/api/v1/chat/rooms/${roomId}/messages/?subdomain=${encodeURIComponent(activeSubdomain)}`,
          {
            headers: {
              Accept: "application/json",
              "X-Tenant-Subdomain": activeSubdomain,
              ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            },
          }
        );
        if (res.ok) {
          const data = await res.json();
          const list = Array.isArray(data) ? data : data.results || [];
          if (list.length > 0) {
            setMessages((prev) => {
              const tempItems = prev.filter(
                (m) => String(m.id).startsWith("temp_") && !list.some((lm: any) => lm.text === m.text)
              );
              return [...list, ...tempItems];
            });
          }
        }
      } catch {}
    }, 3500);
    return () => clearInterval(interval);
  }, [isOpen, roomId, user, apiBase, activeSubdomain, getSafeAuthToken]);

  const handleToggleWidget = () => {
    if (!user) {
      if (openAuthModal) openAuthModal("login");
      else window.dispatchEvent(new CustomEvent("openAuthModal"));
      return;
    }
    setIsOpen((prev) => !prev);
  };

  const initiateCall = async (type: "audio" | "video") => {
    if (!user || !socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) return;
    setCallType(type);
    setCallState("calling");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: type === "video" });
      localStreamRef.current = stream;
      const pc = createPeerConnection(socketRef.current, type);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socketRef.current.send(
        JSON.stringify({ type: "call_offer", data: { call_type: type, sdp: offer, sender_id: user.id } })
      );
    } catch {
      alert("Microphone ba Camera permission allow kora lagbe.");
      cleanupMedia();
      setCallState("idle");
    }
  };

  const acceptCall = async () => {
    if (!user || !socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) return;
    setCallState("active");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: callType === "video",
      });
      localStreamRef.current = stream;
      const pc = createPeerConnection(socketRef.current, callType);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      const pendingOffer = (window as any).__pendingOfferWidget;
      if (pendingOffer) {
        await pc.setRemoteDescription(new RTCSessionDescription(pendingOffer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socketRef.current.send(
          JSON.stringify({ type: "call_answer", data: { sdp: answer, sender_id: user.id } })
        );
        while (pendingCandidatesRef.current.length > 0) {
          const cand = pendingCandidatesRef.current.shift();
          if (cand) await pc.addIceCandidate(new RTCIceCandidate(cand));
        }
      }
    } catch {
      cleanupMedia();
      setCallState("idle");
    }
  };

  const endCall = () => {
    if (!user) return;
    let finalStatus = "completed";
    if (callState === "calling") finalStatus = "cancelled";
    else if (callState === "ringing") finalStatus = "missed";
    socketRef.current?.send(
      JSON.stringify({
        type: "call_ended",
        data: { sender_id: user.id, call_type: callType, duration: callDuration, status: finalStatus },
      })
    );
    cleanupMedia();
    setCallState("idle");
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getAudioTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setIsMuted(!track.enabled);
      }
    }
  };

  const toggleVideoMute = () => {
    if (localStreamRef.current) {
      const track = localStreamRef.current.getVideoTracks()[0];
      if (track) {
        track.enabled = !track.enabled;
        setIsVideoMuted(!track.enabled);
      }
    }
  };

  const submitQueryText = async (outgoingText: string) => {
    const cleanText = outgoingText.trim();
    if (!cleanText || !roomId || !user) return;

    const tempId = `temp_${Date.now()}`;
    const optimisticMsg = {
      id: tempId,
      room: roomId,
      sender: user.id,
      sender_id: user.id,
      sender_role: "customer",
      text: cleanText,
      message_type: "text",
      created_at: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, optimisticMsg]);
    setIsBotTyping(true);

    try {
      const authToken = getSafeAuthToken();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "X-Tenant-Subdomain": activeSubdomain,
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      };

      let res = await fetch(
        `${apiBase}/api/v1/chat/rooms/${roomId}/bot-ask/?subdomain=${encodeURIComponent(activeSubdomain)}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            text: cleanText,
            sender_id: user.id,
            sender_role: "customer",
            email: user.email,
            save_user_message: true,
            subdomain: activeSubdomain,
          }),
        }
      );

      if (!res.ok) {
        res = await fetch(
          `${apiBase}/api/v1/chat/rooms/${roomId}/messages/?subdomain=${encodeURIComponent(activeSubdomain)}`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({
              text: cleanText,
              sender_id: user.id,
              sender_role: "customer",
              email: user.email,
              subdomain: activeSubdomain,
            }),
          }
        );
      }

      if (res.ok) {
        const data = await res.json();
        const savedUserMsg =
          data.user_message || (!data.is_bot && data.sender_role !== "merchant" && data.id ? data : null);
        const botReplyMsg =
          data.bot_reply || ((data.is_bot || data.sender_role === "merchant") && data.id ? data : null);

        setMessages((prev) => {
          const withoutTemp = prev.filter((m) => m.id !== tempId);
          const next = [...withoutTemp];
          if (savedUserMsg && !next.some((m) => String(m.id) === String(savedUserMsg.id))) {
            next.push(savedUserMsg);
          } else if (!savedUserMsg && !next.some((m) => m.text === cleanText && m.sender_role === "customer")) {
            next.push(optimisticMsg);
          }
          if (botReplyMsg && !next.some((m) => String(m.id) === String(botReplyMsg.id))) {
            next.push(botReplyMsg);
          }
          return next;
        });
      } else if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(
          JSON.stringify({
            type: "chat_message",
            text: cleanText,
            sender_id: user.id,
            sender_role: "customer",
          })
        );
      }
    } catch (err) {
      console.error("Failed to send chat message:", err);
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(
          JSON.stringify({
            type: "chat_message",
            text: cleanText,
            sender_id: user.id,
            sender_role: "customer",
          })
        );
      }
    } finally {
      setIsBotTyping(false);
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const outgoingText = text.trim();
    if (!outgoingText) return;
    setText("");
    await submitQueryText(outgoingText);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !roomId) return;

    setIsUploading(true);
    const ext = file.name.split(".").pop()?.toLowerCase() || "";

    let msgType = "document";
    if (file.type.startsWith("image/") || ["png", "jpg", "jpeg", "webp", "svg", "gif"].includes(ext)) {
      msgType = "image";
    } else if (file.type.startsWith("video/") || ["mp4", "webm", "mov", "mkv", "avi"].includes(ext)) {
      msgType = "video";
    } else if (file.type.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a", "aac"].includes(ext)) {
      msgType = "audio";
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", msgType);
    formData.append("sender_role", "customer");

    const authToken = getSafeAuthToken();
    try {
      const res = await fetch(
        `${apiBase}/api/v1/chat/rooms/${roomId}/upload/?subdomain=${encodeURIComponent(activeSubdomain)}`,
        {
          method: "POST",
          headers: {
            "X-Tenant-Subdomain": activeSubdomain,
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
          body: formData,
        }
      );

      if (res.ok) {
        const messageData = await res.json();
        socketRef.current?.send(JSON.stringify({ type: "chat_file", message: messageData }));
        setMessages((prev) => [...prev, messageData]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mimeType = "audio/webm";
      if (typeof MediaRecorder !== "undefined" && typeof MediaRecorder.isTypeSupported === "function") {
        if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) mimeType = "audio/webm;codecs=opus";
        else if (MediaRecorder.isTypeSupported("audio/mp4")) mimeType = "audio/mp4";
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      isCancelledRef.current = false;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        if (isCancelledRef.current) return;

        const ext = mimeType.includes("mp4") ? "mp4" : "webm";
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        if (audioBlob.size > 0 && roomId) {
          const audioFile = new File([audioBlob], `voice_${Date.now()}.${ext}`, { type: mimeType });
          setIsUploading(true);
          const formData = new FormData();
          formData.append("file", audioFile);
          formData.append("type", "audio");
          formData.append("sender_role", "customer");

          const authToken = getSafeAuthToken();
          try {
            const res = await fetch(
              `${apiBase}/api/v1/chat/rooms/${roomId}/upload/?subdomain=${encodeURIComponent(activeSubdomain)}`,
              {
                method: "POST",
                headers: {
                  "X-Tenant-Subdomain": activeSubdomain,
                  ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
                },
                body: formData,
              }
            );

            if (res.ok) {
              const messageData = await res.json();
              socketRef.current?.send(JSON.stringify({ type: "chat_file", message: messageData }));
              setMessages((prev) => [...prev, messageData]);
            }
          } catch (err) {
            console.error(err);
          } finally {
            setIsUploading(false);
          }
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => setRecordingDuration((p) => p + 1), 1000);
    } catch {
      alert("Microphone permission allow kora lagbe.");
    }
  };

  const stopAndSendRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      clearInterval(recordingTimerRef.current);
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      isCancelledRef.current = true;
      clearInterval(recordingTimerRef.current);
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setRecordingDuration(0);
    }
  };

  return (
    <>
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />

      {!isOpen && (
        <button
          onClick={handleToggleWidget}
          className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 w-13 h-13 sm:w-14 sm:h-14 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center shadow-2xl hover:scale-105 active:scale-95 transition-all cursor-pointer group"
          title="Chat with Store Support"
        >
          <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7 group-hover:rotate-12 transition-transform" />
          {user && (
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-white animate-pulse" />
          )}
        </button>
      )}

      {isOpen && user && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 z-50 w-full h-full sm:w-[410px] sm:h-[610px] sm:max-h-[86vh] bg-slate-900 border border-slate-800 sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden font-sans animate-in slide-in-from-bottom-5">
          {callState !== "idle" && (
            <div className="absolute inset-0 z-50 bg-slate-950/95 backdrop-blur-xl flex flex-col items-center justify-between py-10 px-6 text-white animate-in fade-in">
              <div className="text-center space-y-1">
                <span className="text-[9px] uppercase font-black tracking-widest text-emerald-400 bg-emerald-950/80 px-3 py-1 rounded-full border border-emerald-800">
                  {callType.toUpperCase()} CALL
                </span>
                <h2 className="text-xl font-black mt-2">{merchantName}</h2>
                <p className="text-xs text-slate-400">
                  {callState === "calling" && `Calling ${callType}...`}
                  {callState === "ringing" && `Incoming ${callType} call...`}
                  {callState === "active" && formatTimer(callDuration)}
                </p>
              </div>

              <div className="relative my-auto flex items-center justify-center">
                {callType === "video" && callState === "active" ? (
                  <div className="relative w-64 h-80 max-w-[80vw] bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 shadow-xl">
                    <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />
                    <video
                      ref={localVideoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-20 h-28 object-cover absolute bottom-2 right-2 rounded-xl border border-emerald-500 bg-black"
                    />
                  </div>
                ) : (
                  <div className="relative flex items-center justify-center">
                    {(callState === "ringing" || callState === "calling") && (
                      <div className="absolute w-36 h-36 rounded-full bg-emerald-500/20 animate-ping" />
                    )}
                    <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-emerald-600 to-teal-400 flex items-center justify-center text-3xl font-black shadow-xl shadow-emerald-500/30 border-2 border-slate-800 z-10">
                      {merchantName.charAt(0).toUpperCase()}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-5">
                {callState === "ringing" && (
                  <>
                    <button
                      onClick={endCall}
                      className="w-14 h-14 bg-rose-600 hover:bg-rose-500 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110 cursor-pointer"
                    >
                      <PhoneOff className="w-6 h-6 text-white" />
                    </button>
                    <button
                      onClick={acceptCall}
                      className="w-14 h-14 bg-emerald-600 hover:bg-emerald-500 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110 animate-bounce cursor-pointer"
                    >
                      <PhoneCall className="w-6 h-6 text-white" />
                    </button>
                  </>
                )}
                {callState === "calling" && (
                  <button
                    onClick={endCall}
                    className="w-14 h-14 bg-rose-600 hover:bg-rose-500 rounded-full flex items-center justify-center shadow-xl transition-transform hover:scale-110 cursor-pointer"
                  >
                    <PhoneOff className="w-6 h-6 text-white" />
                  </button>
                )}
                {callState === "active" && (
                  <>
                    <button
                      onClick={toggleMute}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                        isMuted ? "bg-rose-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                      }`}
                    >
                      <MicOff className="w-5 h-5" />
                    </button>
                    {callType === "video" && (
                      <button
                        onClick={toggleVideoMute}
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all cursor-pointer ${
                          isVideoMuted ? "bg-rose-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                        }`}
                      >
                        <VideoOff className="w-5 h-5" />
                      </button>
                    )}
                    <button
                      onClick={endCall}
                      className="w-14 h-14 bg-rose-600 hover:bg-rose-500 rounded-full flex items-center justify-center shadow-xl transition-transform hover:scale-110 cursor-pointer"
                    >
                      <PhoneOff className="w-6 h-6 text-white" />
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Header */}
          <div className="px-4 py-3.5 bg-slate-950 border-b border-slate-800 flex justify-between items-center flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-sm flex-shrink-0">
                {merchantName.charAt(0).toUpperCase()}
              </div>
              <div className="truncate">
                <h3 className="font-bold text-white text-xs truncate max-w-[160px]">{merchantName}</h3>
                <span className="text-[10px] text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> AI & Live Store Support
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1 text-slate-400">
              <button
                onClick={() => initiateCall("audio")}
                className="p-2 hover:bg-slate-800 hover:text-white rounded-xl transition-all cursor-pointer"
                title="Voice Call"
              >
                <Phone className="w-4 h-4" />
              </button>
              <button
                onClick={() => initiateCall("video")}
                className="p-2 hover:bg-slate-800 hover:text-white rounded-xl transition-all cursor-pointer"
                title="Video Call"
              >
                <Video className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-slate-800 hover:text-white rounded-xl transition-all cursor-pointer"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages Stream */}
          <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-3 bg-slate-900/50 overscroll-contain touch-pan-y">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
              </div>
            ) : messages.length === 0 ? (
              <div className="text-center text-xs text-slate-500 my-16">
                Say hello or tap a quick topic below to get instant store info!
              </div>
            ) : (
              messages.map((msg, idx) => {
                const senderVal = msg.sender_id ?? msg.sender;
                const isFromMerchantOrBot =
                  Boolean(msg.is_bot) ||
                  msg.sender_role === "merchant" ||
                  (typeof msg.text === "string" &&
                    (msg.text.startsWith("👋") ||
                      msg.text.startsWith("🏢") ||
                      msg.text.startsWith("🏪") ||
                      msg.text.includes("👉 [পণ্যটি দেখতে") ||
                      msg.text.includes("👉 [View Product"))) ||
                  (merchantId !== null && String(senderVal) === String(merchantId)) ||
                  (merchantId !== null && String(msg.sender) === String(merchantId));
                const isMe =
                  !isFromMerchantOrBot &&
                  (msg.sender_role === "customer" ||
                    String(senderVal) === String(user.id) ||
                    String(msg.sender) === String(user.id) ||
                    String(msg.id || "").startsWith("temp_") ||
                    (msg.sender_details?.email &&
                      user.email &&
                      msg.sender_details.email.toLowerCase() === user.email.toLowerCase()));
                const ext = msg.file_name?.split(".").pop()?.toLowerCase() || "";
                const mediaUrl = formatMediaUrl(msg.file_url);

                if (msg.message_type === "call") {
                  const isMissed =
                    (msg.text || "").toLowerCase().includes("cancelled") ||
                    (msg.text || "").toLowerCase().includes("missed");
                  return (
                    <div key={idx} className="flex justify-center my-2">
                      <div className="flex items-center gap-2.5 px-3.5 py-1.5 rounded-xl bg-slate-950 border border-slate-800 text-[11px] shadow-sm">
                        <div
                          className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            isMissed ? "bg-rose-500/20 text-rose-400" : "bg-emerald-500/20 text-emerald-400"
                          }`}
                        >
                          {isMissed ? (
                            <PhoneMissed className="w-3.5 h-3.5" />
                          ) : (
                            <PhoneIncoming className="w-3.5 h-3.5" />
                          )}
                        </div>
                        <div>
                          <p className={`font-semibold ${isMissed ? "text-rose-400" : "text-slate-200"}`}>
                            {msg.text}
                          </p>
                          <span className="text-[9px] text-slate-500">{formatValidDate(msg.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={idx} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[86%] sm:max-w-[82%] rounded-2xl p-3 text-xs shadow-md ${
                        isMe
                          ? "bg-emerald-600 text-white rounded-br-none"
                          : "bg-slate-800 text-slate-200 rounded-bl-none border border-slate-700/60"
                      }`}
                    >
                      {mediaUrl && (
                        <div className="mb-2">
                          {msg.message_type === "image" ? (
                            <a href={mediaUrl} target="_blank" rel="noreferrer">
                              <img
                                src={mediaUrl}
                                alt="attachment"
                                className="rounded-xl max-h-52 object-contain hover:opacity-90 bg-slate-950/40"
                              />
                            </a>
                          ) : msg.message_type === "video" ? (
                            <video src={mediaUrl} controls className="rounded-xl max-h-52 bg-black/40 w-full" />
                          ) : msg.message_type === "audio" ? (
                            <div className="flex items-center gap-2 p-1.5 rounded-xl min-w-[190px] sm:min-w-[210px] bg-black/20">
                              <div className="w-7 h-7 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center flex-shrink-0">
                                <Mic className="w-3.5 h-3.5" />
                              </div>
                              <audio src={mediaUrl} controls preload="auto" className="h-8 flex-1 max-w-[170px]" />
                            </div>
                          ) : (
                            <a
                              href={mediaUrl}
                              target="_blank"
                              rel="noreferrer"
                              className={`flex items-center gap-2.5 p-2.5 rounded-xl transition-all ${
                                isMe ? "bg-emerald-700/60 hover:bg-emerald-700" : "bg-slate-700/60 hover:bg-slate-700"
                              }`}
                            >
                              {["xlsx", "xls", "csv"].includes(ext) ? (
                                <FileSpreadsheet className="w-6 h-6 text-emerald-300 flex-shrink-0" />
                              ) : ["ai", "psd", "eps"].includes(ext) ? (
                                <FileCode className="w-8 h-8 text-amber-300 flex-shrink-0" />
                              ) : (
                                <FileText className="w-8 h-8 text-cyan-300 flex-shrink-0" />
                              )}
                              <div className="overflow-hidden flex-1 min-w-0">
                                <p className="truncate font-bold text-xs">{msg.file_name || "Attachment"}</p>
                                <p className="text-[9px] opacity-75 uppercase">{ext || "FILE"} • Download</p>
                              </div>
                              <Download className="w-4 h-4 opacity-75 flex-shrink-0" />
                            </a>
                          )}
                        </div>
                      )}

                      {msg.text && <FormattedChatText content={msg.text} />}

                      <div
                        className={`text-[9px] mt-1 flex items-center justify-end gap-1 ${
                          isMe ? "text-emerald-200" : "text-slate-400"
                        }`}
                      >
                        {formatValidDate(msg.created_at)}
                        {isMe && <CheckCheck className="w-3 h-3" />}
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {isBotTyping && (
              <div className="flex justify-start items-center gap-2 py-1 animate-in fade-in">
                <div className="bg-slate-800 text-slate-300 rounded-2xl rounded-bl-none px-3.5 py-2 flex items-center gap-2 text-xs shadow-md border border-slate-700/50">
                  <Bot className="w-3.5 h-3.5 text-emerald-400 animate-spin" />
                  <span className="text-[11px] text-slate-300 font-medium">AI Assistant is checking store...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Interactive Store Quick-Action Chips Bar */}
          {!isRecording && quickPrompts.length > 0 && (
            <div className="px-3 py-2 bg-slate-950/90 border-t border-slate-800/80 flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-shrink-0">
              <Sparkles className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mr-0.5" />
              {quickPrompts.map((item, qIdx) => (
                <button
                  key={qIdx}
                  type="button"
                  disabled={isBotTyping || !roomId}
                  onClick={() => submitQueryText(item.prompt)}
                  className="px-2.5 py-1 rounded-full bg-slate-900 hover:bg-emerald-950/80 text-slate-300 hover:text-emerald-300 border border-slate-800 hover:border-emerald-500/40 text-[10px] font-medium whitespace-nowrap transition-all cursor-pointer active:scale-95 disabled:opacity-50"
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}

          {/* Input Box */}
          <div className="p-3 bg-slate-950 border-t border-slate-800 flex-shrink-0">
            {isRecording ? (
              <div className="flex items-center justify-between bg-slate-900 border border-rose-500/40 rounded-full px-4 py-2 animate-in fade-in">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping" />
                  <span className="text-xs font-bold text-white tracking-wide">
                    Recording: {formatTimer(recordingDuration)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={cancelRecording}
                    className="p-1.5 text-rose-400 hover:bg-rose-500/10 rounded-full transition-colors cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={stopAndSendRecording}
                    className="p-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-full shadow-lg cursor-pointer"
                  >
                    <Send className="w-3.5 h-3.5 ml-0.5" />
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={sendMessage} className="flex items-center gap-2">
                <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" accept="*/*" />
                <button
                  type="button"
                  disabled={isUploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-colors cursor-pointer disabled:opacity-50 flex-shrink-0"
                  title="Attach Any File"
                >
                  {isUploading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
                  ) : (
                    <Paperclip className="w-4 h-4" />
                  )}
                </button>

                <input
                  type="text"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="পণ্যের নাম, দাম বা অর্ডার সম্পর্কে লিখুন..."
                  className="flex-1 py-2 px-4 bg-slate-900 border border-slate-800 rounded-full text-xs text-white outline-none focus:border-emerald-500"
                />

                {text.trim() ? (
                  <button
                    type="submit"
                    className="p-2 bg-emerald-600 text-white rounded-full hover:bg-emerald-500 transition-colors cursor-pointer shadow-md flex-shrink-0"
                  >
                    <Send className="w-3.5 h-3.5 ml-0.5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startRecording}
                    className="p-2 bg-slate-800 text-emerald-400 hover:bg-emerald-600 hover:text-white rounded-full transition-all cursor-pointer flex-shrink-0"
                  >
                    <Mic className="w-3.5 h-3.5" />
                  </button>
                )}
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}